import { useState } from 'react';
import { AppWindow, Check, ChevronDown, Trash2 } from 'lucide-react';
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ideoz/ui';
import {
  relativeTime,
  type VersionMenuEntry,
  type VersionMenuGroup,
} from '../utils/prototypeVersionMenu';

/** Versions listed per prototype before "Show all". */
const VERSIONS_SHOWN = 5;

export interface PrototypeVersionMenuProps {
  /** The panel title: the version on screen, or the build running. */
  title: string;
  groups: VersionMenuGroup[];
  /** Why other versions cannot be opened now (a build is running), or null. */
  disabledReason?: string | null;
  onOpen: (entry: VersionMenuEntry) => void;
  onContinue?: (entry: VersionMenuEntry) => void;
  /**
   * Remove a build that did not finish. The row asks "Remove?" first. Without it the
   * button is not shown.
   */
  onRemove?: (entry: VersionMenuEntry) => void | Promise<unknown>;
  className?: string;
  testId?: string;
}

/**
 * The panel title as a menu of the conversation's prototypes and their versions (plan: title
 * dropdown). Picking a version opens it the way clicking its card in the chat does.
 */
export function PrototypeVersionMenu({
  title,
  groups,
  disabledReason = null,
  onOpen,
  onContinue,
  onRemove,
  className,
  testId = 'prototype-version-menu',
}: PrototypeVersionMenuProps): JSX.Element {
  const [showAll, setShowAll] = useState<Record<string, boolean>>({});
  /** The row asking "Remove?", and the one whose removal is on its way. */
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const remove = async (entry: VersionMenuEntry): Promise<void> => {
    setRemovingId(entry.llmInteractionId);
    try {
      await onRemove?.(entry);
    } finally {
      setRemovingId(null);
      setConfirmingId(null);
    }
  };
  const hasEntries = groups.some((g) => g.entries.length > 0);

  const heading = (
    <>
      <AppWindow size={16} className="text-primary-500 shrink-0" />
      <h2 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
    </>
  );
  if (!hasEntries) {
    return <div className={cn('flex min-w-0 items-center gap-2', className)}>{heading}</div>;
  }

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) setConfirmingId(null);
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex min-w-0 items-center gap-2 rounded-md px-1.5 py-1 -mx-1.5 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 dark:hover:bg-gray-800',
            className
          )}
          aria-label={`${title}. Switch prototype or version`}
          data-testid={testId}
        >
          {heading}
          <ChevronDown size={14} className="shrink-0 text-gray-500" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[70vh] w-80 overflow-y-auto"
        data-testid={`${testId}-content`}
      >
        {disabledReason ? (
          <p className="px-2 py-1.5 text-xs text-gray-500">{disabledReason}</p>
        ) : null}
        {groups.map((group, gi) => {
          const expanded = showAll[group.key] === true;
          const entries = expanded ? group.entries : group.entries.slice(0, VERSIONS_SHOWN);
          const hidden = group.entries.length - entries.length;
          return (
            <div key={group.key}>
              {gi > 0 ? <DropdownMenuSeparator /> : null}
              <DropdownMenuLabel className="truncate text-xs font-semibold text-gray-700 dark:text-gray-300">
                {group.name}
              </DropdownMenuLabel>
              {entries.map((entry) => {
                const when = relativeTime(entry.createdAt);
                const building = entry.state === 'building';
                const unfinished = entry.state === 'unfinished';
                const disabled = building || (disabledReason != null && !entry.isCurrent);
                // Nothing is removed while a build runs: it may be this very one.
                const removable = unfinished && entry.canRemove && onRemove != null && !disabledReason;
                if (removable && confirmingId === entry.llmInteractionId) {
                  const removing = removingId === entry.llmInteractionId;
                  // Not a menu item: its buttons must not open the version or close the menu.
                  return (
                    <div
                      key={entry.llmInteractionId}
                      className="flex items-center gap-2 py-1.5 pl-9 pr-2"
                      data-testid={`${testId}-remove-confirm`}
                    >
                      <span className="min-w-0 flex-1 truncate text-sm text-gray-700 dark:text-gray-300">
                        Remove?
                      </span>
                      <button
                        type="button"
                        disabled={removing}
                        className="shrink-0 rounded-md bg-error-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-error-700 disabled:opacity-60"
                        onClick={() => void remove(entry)}
                        data-testid={`${testId}-remove-yes`}
                      >
                        {removing ? 'Removing…' : 'Remove'}
                      </button>
                      <button
                        type="button"
                        disabled={removing}
                        className="shrink-0 rounded-md border border-gray-300 px-2 py-0.5 text-[11px] font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                        onClick={() => setConfirmingId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  );
                }
                return (
                  <DropdownMenuItem
                    key={entry.llmInteractionId}
                    disabled={disabled}
                    onSelect={() => {
                      if (!entry.isCurrent) onOpen(entry);
                    }}
                    className="flex items-center gap-2 pl-3"
                    data-testid={`${testId}-entry`}
                  >
                    <span className="flex w-4 shrink-0 justify-center">
                      {entry.isCurrent ? <Check size={14} className="text-primary-600" /> : null}
                    </span>
                    <span
                      className={cn(
                        'shrink-0 text-sm',
                        entry.isCurrent ? 'font-semibold' : 'font-medium',
                        unfinished && 'text-warning-600'
                      )}
                    >
                      {entry.label}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-xs text-gray-500">
                        {[when, entry.isPublished ? 'Published' : null].filter(Boolean).join(' · ')}
                      </span>
                      {entry.madeIn ? (
                        // Scope names are long: on a line of their own, the whole name on hover.
                        <span
                          className="truncate text-[11px] text-gray-400"
                          title={entry.madeIn}
                          data-testid={`${testId}-made-in`}
                        >
                          {entry.madeIn}
                        </span>
                      ) : null}
                    </span>
                    {unfinished && entry.canContinue && onContinue && !disabledReason ? (
                      <button
                        type="button"
                        className="shrink-0 rounded-md bg-primary-900 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-primary-800"
                        onClick={(e) => {
                          e.stopPropagation();
                          onContinue(entry);
                        }}
                      >
                        {entry.action === 'build-again' ? 'Build again' : 'Continue'}
                      </button>
                    ) : null}
                    {removable ? (
                      <button
                        type="button"
                        className="shrink-0 rounded-md p-1 text-gray-500 hover:bg-gray-200 hover:text-error-600 dark:hover:bg-gray-700"
                        aria-label="Remove this build"
                        title="Remove this build"
                        onClick={(e) => {
                          // The row must not open, and the menu stays for the question.
                          e.stopPropagation();
                          setConfirmingId(entry.llmInteractionId);
                        }}
                        data-testid={`${testId}-remove`}
                      >
                        <Trash2 size={13} aria-hidden="true" />
                      </button>
                    ) : null}
                  </DropdownMenuItem>
                );
              })}
              {hidden > 0 ? (
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setShowAll((s) => ({ ...s, [group.key]: true }));
                  }}
                  className="pl-9 text-xs text-primary-700"
                >
                  Show all {group.entries.length} versions
                </DropdownMenuItem>
              ) : null}
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default PrototypeVersionMenu;
